/**
 * StepCard — the numbered write-surface card (`forms.html` `.card.now`/`.card.next`
 * + `.step-n`; WEB TIER A · WT5, plan §2's `NumberedStepCard`).
 *
 * The approved forms mockup numbers a write surface's cards with the SAME
 * vocabulary the record's lifecycle rail uses ("① is complete and owns its own
 * Change action, ② is current and marked by a left edge, ③ is optional and
 * marked by a dashed rim with its words at full contrast. Nothing on the page
 * is dimmed."):
 *
 *  · `done` — the number chip takes the ok pair (`status/ok` bg + fg ring);
 *  · `now`  — a 3px `status/info` left edge on the card, the chip in the info
 *    pair. An EDGE, not a fill: the mockup's own measurement is that a tinted
 *    card would drop its quiet text to ~4.1;
 *  · `next` — the whole rim goes DASHED `border/strong` and so does the chip.
 *    MC-7's ruling: inert is a border, never an opacity and never muted text —
 *    the card's words stay at full contrast.
 *
 * The title is an `h2` for the same reason `RecordCard`'s is: the numbered
 * cards ARE the page's outline. The header row reuses RecordCard's 52px
 * geometry so a record page and a write page rhyme.
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type StepCardState = 'done' | 'now' | 'next';

const CHIP: Record<StepCardState, string> = {
  done: 'border-ok-fg bg-ok-bg text-ok-fg',
  now: 'border-info-fg bg-info-bg text-info-fg',
  next: 'border-dashed border-line-strong bg-transparent text-fg',
};

export interface StepCardProps {
  /** The ①②③ ordinal. */
  n: number;
  state: StepCardState;
  title: string;
  /** A tone pill beside the title ("Chosen" / "Current" / "Optional"). */
  badge?: ReactNode;
  /** A control on the header row's right (the mockup's "Change"). */
  headerAction?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function StepCard({
  n,
  state,
  title,
  badge,
  headerAction,
  children,
  className,
}: StepCardProps): React.JSX.Element {
  return (
    <section
      className={cn(
        'overflow-hidden rounded-md border bg-surface',
        state === 'now' && 'border-line border-l-[3px] border-l-info-fg',
        state === 'next' && 'border-dashed border-line-strong',
        state === 'done' && 'border-line',
        className,
      )}
    >
      <header className="flex min-h-[52px] items-center justify-between gap-4 border-b border-line px-4 py-3">
        <span className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden="true"
            className={cn(
              'inline-flex size-6 shrink-0 items-center justify-center rounded-pill border text-label font-semibold tnum',
              CHIP[state],
            )}
          >
            {n}
          </span>
          <h2 className="truncate text-h3 text-fg">{title}</h2>
          {badge}
        </span>
        {headerAction === undefined ? null : (
          <span className="flex shrink-0 items-center gap-2">{headerAction}</span>
        )}
      </header>
      <div className="flex flex-col gap-3.5 p-4">{children}</div>
    </section>
  );
}
